import {
  ArrowDownward,
  ArrowUpward,
  BarChartOutlined,
  ChatBubbleOutline,
  ManageSearchOutlined,
  ScienceOutlined,
  WarningAmberOutlined
} from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import React from "react";

export type SummaryBullet = {
  text: string;
  level: number;
  kind?: "heading" | "paragraph" | "bullet";
};

type SummaryBlock = { kind: "paragraph"; text: string } | { kind: "list"; items: SummaryBullet[] };
type SummarySection = { heading?: string; blocks: SummaryBlock[] };

const tones = {
  red: { color: "#C50F1F", background: "#FDE7E9" },
  green: { color: "#107C10", background: "#EAF6EA" },
  amber: { color: "#A84700", background: "#FFF1CC" },
  blue: { color: "#005A9E", background: "#DDEEFF" },
  neutral: { color: "#616161", background: "#F0F0F0" }
};

const Highlight: React.FC<{ tone: keyof typeof tones; children: React.ReactNode }> = ({ tone, children }) => (
  <Box
    component="span"
    sx={{
      color: tones[tone].color,
      bgcolor: tones[tone].background,
      borderRadius: 0.5,
      px: 0.5,
      py: 0.15,
      fontWeight: 700,
      boxDecorationBreak: "clone",
      WebkitBoxDecorationBreak: "clone"
    }}
  >
    {children}
  </Box>
);

const emphasizeTenantCount = (text: string) => {
  const count = /^\d[\d,.]*(?=\s+customer tenants\b)/i.exec(text);
  return count ? (
    <>
      <strong>{count[0]}</strong>
      {text.slice(count[0].length)}
    </>
  ) : (
    text
  );
};

const renderBulletText = (text: string, heading: string) => {
  const labeled = /^Needs attention\b/i.test(heading) ? null : /^([^:]+):\s*(.+)$/.exec(text);
  const body = labeled ? labeled[2] : text;
  let content: React.ReactNode = body;

  if (/^Usage\b/i.test(heading)) {
    const trend =
      /\b(up|down)\s+(\d+(?:\.\d+)?)%(?:\s+(?:from\s+[^.!?\n]+|MoM\b|WoW\b|month[- ]over[- ]month\b|week[- ]over[- ]week\b))?[.!?]?/i.exec(body);
    if (trend && Number(trend[2]) !== 0) {
      const direction = trend[1].toLowerCase();
      const TrendIcon = direction === "down" ? ArrowDownward : ArrowUpward;
      content = (
        <>
          {emphasizeTenantCount(body.slice(0, trend.index))}
          <Highlight tone={direction === "down" ? "red" : "green"}>
            <TrendIcon aria-hidden="true" sx={{ fontSize: 17, verticalAlign: "text-bottom", mr: 0.25 }} />
            {trend[0]}
          </Highlight>
          {body.slice(trend.index + trend[0].length)}
        </>
      );
    } else {
      content = emphasizeTenantCount(body);
    }
  } else if (/^Customer feedback\b/i.test(heading) && /^Leading customer needs$/i.test(labeled?.[1] ?? "")) {
    content = <Highlight tone="amber">{body}</Highlight>;
  } else if (/^Competitive insights\b/i.test(heading)) {
    const priorities = /^(Competitive priorities include\s+)(.+)$/i.exec(body);
    if (priorities)
      content = (
        <>
          {priorities[1]}
          <Highlight tone="blue">{priorities[2]}</Highlight>
        </>
      );
  }

  return labeled ? (
    <>
      <strong>{labeled[1]}:</strong> {content}
    </>
  ) : (
    content
  );
};

const getSectionStyle = (heading: string, text: string) => {
  if (/^Usage\b/i.test(heading)) {
    const trend = /\b(up|down)\s+(\d+(?:\.\d+)?)%/i.exec(text);
    const tone = !trend
      ? "blue"
      : Number(trend[2]) === 0
        ? "neutral"
        : trend[1].toLowerCase() === "down"
          ? "red"
          : "green";
    return { icon: BarChartOutlined, ...tones[tone] };
  }
  if (/^Customer feedback\b/i.test(heading)) return { icon: ChatBubbleOutline, ...tones.amber };
  if (/^UX research\b/i.test(heading)) return { icon: ScienceOutlined, color: "#6B3FB5", background: "#F2ECFC" };
  if (/^Needs attention\b/i.test(heading)) {
    const count = /\b(\d[\d,]*)\s+items?\b.*needs[- ]attention/i.exec(text);
    return {
      icon: WarningAmberOutlined,
      ...tones[count && Number(count[1].replace(/,/g, "")) > 0 ? "red" : "neutral"]
    };
  }
  return { icon: ManageSearchOutlined, ...tones.blue };
};

const ExecutiveSummarySections: React.FC<{ items: SummaryBullet[] }> = ({ items }) => {
  let section: SummarySection = { blocks: [] };
  const sections = [section];
  for (const item of items) {
    if (item.kind === "heading") {
      section = { heading: item.text, blocks: [] };
      sections.push(section);
    } else if (item.kind === "bullet") {
      const lastBlock = section.blocks[section.blocks.length - 1];
      if (lastBlock?.kind === "list") lastBlock.items.push(item);
      else section.blocks.push({ kind: "list", items: [item] });
    } else {
      if (/^Explore the visualizations below\b/i.test(item.text)) {
        section = { blocks: [] };
        sections.push(section);
      }
      section.blocks.push({ kind: "paragraph", text: item.text });
    }
  }

  return (
    <Box sx={{ display: "grid", gap: 2, color: "#091F3A" }}>
      {sections
        .filter(({ heading, blocks }) => heading || blocks.length > 0)
        .map(({ heading, blocks }, sectionIndex) => {
          const style = getSectionStyle(
            heading ?? "",
            blocks.flatMap((block) => (block.kind === "list" ? block.items.map(({ text }) => text) : [])).join(" ")
          );
          const Icon = style.icon;
          return (
            <Box
              key={`${sectionIndex}-${heading ?? "intro"}`}
              sx={{
                display: "grid",
                gridTemplateColumns: heading
                  ? { xs: "36px minmax(0, 1fr)", sm: "44px minmax(0, 1fr)" }
                  : "minmax(0, 1fr)",
                columnGap: { xs: 1.25, sm: 2 },
                alignItems: "start"
              }}
            >
              {heading && (
                <Box
                  aria-hidden="true"
                  sx={{
                    width: { xs: 36, sm: 44 },
                    height: { xs: 36, sm: 44 },
                    display: "grid",
                    placeItems: "center",
                    borderRadius: 1.5,
                    bgcolor: style.background,
                    color: style.color
                  }}
                >
                  <Icon sx={{ fontSize: { xs: 25, sm: 30 } }} />
                </Box>
              )}
              <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                {heading && (
                  <Typography component="h3" sx={{ m: 0, mb: 0.25, fontSize: 15, fontWeight: 800, lineHeight: 1.5 }}>
                    {heading}
                  </Typography>
                )}
                {blocks.map((block, blockIndex) =>
                  block.kind === "list" ? (
                    <Box component="ul" key={blockIndex} sx={{ m: 0, pl: 2.5, fontSize: 14, lineHeight: 1.65 }}>
                      {block.items.map((item, index) => (
                        <Box
                          component="li"
                          key={index}
                          sx={{ pl: 0.25, ml: Math.max(0, Math.min(item.level - 1, 2)) * 2 }}
                        >
                          {renderBulletText(item.text, heading ?? "")}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography
                      component="p"
                      key={blockIndex}
                      sx={{
                        m: 0,
                        mt: blockIndex > 0 ? 0.5 : 0,
                        fontSize: !heading && sectionIndex === 0 && blockIndex === 0 ? 15 : 14,
                        fontWeight: !heading && sectionIndex === 0 && blockIndex === 0 ? 800 : 400,
                        lineHeight: 1.65
                      }}
                    >
                      {block.text}
                    </Typography>
                  )
                )}
              </Box>
            </Box>
          );
        })}
    </Box>
  );
};

export default ExecutiveSummarySections;
